import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useUserStore } from './stores/useUserStore';
import { useSubscriptionLimitsStore } from './stores/useSubscriptionLimitsStore';
import { useChatStore } from './stores/useChatStore';
import { useTTSStore, type Voice } from './stores/useTTSStore';
import { useTheme } from './hooks/useTheme';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { usePaymentCheck } from './hooks/usePaymentCheck';
// DISABLED: import { useTimeAchievements } from './hooks/useTimeAchievements';
import { validateStorageSync, clearUserStorage, cleanupIfNeeded } from './utils/storageUtils';

// Lazy loaded pages - code splitting для уменьшения начального бандла
const ChatPage = lazy(() => import('./pages/Chat'));
const ImagePage = lazy(() => import('./pages/Image'));
const VideoPage = lazy(() => import('./pages/Video/VideoPage'));
const ToolsPage = lazy(() => import('./pages/Tools'));
const UpscalePage = lazy(() => import('./pages/Upscale'));
const ImageEditPage = lazy(() => import('./pages/ImageEdit'));
const AvatarsPage = lazy(() => import('./pages/Avatars'));
const WardrobePage = lazy(() => import('./pages/Wardrobe'));
const MotionControlPage = lazy(() => import('./pages/MotionControl'));
const VideoRestylePage = lazy(() => import('./pages/VideoRestyle'));
const VisionaryStudioPage = lazy(() => import('./pages/VisionaryStudio'));
const AccountPage = lazy(() => import('./pages/Account'));
const MergeAccountPage = lazy(() => import('./pages/MergeAccount'));
const GalleryPage = lazy(() => import('./pages/Gallery/GalleryPage'));
const TokensPage = lazy(() => import('./pages/TokensPage'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage'));
const SystemPage = lazy(() => import('./pages/SystemPage'));

// Публичные страницы - lazy load
const PublicPricingPage = lazy(() => import('./pages/Pricing'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage'));
const LoginPage = lazy(() => import('./pages/Login'));
const LandingPage = lazy(() => import('./pages/Landing'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy'));
const PublicOfferPage = lazy(() => import('./pages/PublicOffer'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicy'));
const ConsentPage = lazy(() => import('./pages/Consent'));
const CookieBanner = lazy(() => import('./components/CookieBanner/CookieBanner'));
const InviteRedirectPage = lazy(() => import('./pages/InviteRedirectPage'));
const BroadcastCardPreviewPage = lazy(() => import('./pages/BroadcastCardPreviewPage'));

import BottomNav from './components/Navigation/BottomNav';
import { DesktopHeader } from './components/Navigation/DesktopHeader';
import { MobilePublicHeader } from './components/Navigation/MobilePublicHeader';
import { Toast, LoadingScreen, ReferralWelcomeModal, CampaignWelcomeModal, TeamInviteWelcomeModal, WelcomeModal, LowTokensNotification, TeamModeWrapper, UpdateNotification, useUpdateNotification } from './components/Common';
import type { CampaignInfo } from './components/Common';
import { useTeamStore } from './stores/useTeamStore';
import { usePricingStore, initPricingVersionCheck } from './stores/usePricingStore';
import { useWebSocket } from './hooks/useWebSocket';
import NotificationManager from './components/Common/NotificationManager';
import { hasAuthData, getInitData } from './utils/authHeaders';
import { apiClient } from './services/api';
import { amplitude } from './utils/amplitude';
import './App.css';

// Минимальный fallback для Suspense - не показываем ничего, т.к. LoadingScreen уже есть
const PageFallback = () => null;

/**
 * TeamRedirect — обработчик legacy /team/:teamId/* URL.
 * Устанавливает команду в store и делает redirect на чистый URL.
 * Позволяет поддерживать старые ссылки и invite-ссылки.
 */
function TeamRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  const location = useLocation();
  const { setActiveTeam } = useTeamStore();

  useEffect(() => {
    if (teamId) {
      // Сохраняем в store (persist → localStorage)
      setActiveTeam(teamId);
      // Извлекаем чистый путь: /team/xxx/image → /image
      const cleanPath = location.pathname.replace(/^\/team\/[^/]+/, '') || '/chat';
      // Hard reload на чистый URL
      window.location.href = cleanPath;
    }
  }, [teamId, location.pathname, setActiveTeam]);

  return null;
}

interface ReferrerInfo {
  id: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

function AppContent() {
  const navigate = useNavigate();
  const appRef = useRef<HTMLDivElement>(null);
  const { user: userData, setUser, initUser, fetchUser } = useUserStore();
  const { fetchLimits } = useSubscriptionLimitsStore();
  const { isCurrentSessionSecret, setCurrentSessionId } = useChatStore();
  const { setVoice } = useTTSStore();
  useTheme();
  const { isReady, user, isInsideTelegram } = useTelegramWebApp();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // LoadingScreen показываем только в Telegram Mini App (не в браузере)
  // Используем класс is-telegram который устанавливается в index.html
  const isTelegramMiniApp = document.documentElement.classList.contains('is-telegram');
  const [isLoading, setIsLoading] = useState(isTelegramMiniApp);
  const [referrerInfo, setReferrerInfo] = useState<ReferrerInfo | null>(null);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  
  // Очистка localStorage при старте если превышен лимит (важно для Telegram Mini App на iOS)
  useEffect(() => {
    if (isTelegramMiniApp) {
      // Отложенный вызов чтобы не блокировать инициализацию
      const timer = setTimeout(() => {
        cleanupIfNeeded(1536); // 1.5MB лимит для Telegram Mini App
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isTelegramMiniApp]);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showLowTokensNotification, setShowLowTokensNotification] = useState(false);
  const [isFreeUserNotification, setIsFreeUserNotification] = useState(false); // Для free пользователей показываем другое сообщение
  const [teamInviteCode, setTeamInviteCode] = useState<string | null>(null);
  const [showTeamInviteModal, setShowTeamInviteModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const { fetchTeams, setActiveTeam, activeTeam, isTeamMode } = useTeamStore();
  const { loadPricing } = usePricingStore();
  const location = useLocation();
  
  const [searchParams] = useSearchParams();
  const showLoginModal = searchParams.get('auth') === 'login';
  const isLoginPage = location.pathname === '/login';
  const isLandingPage = location.pathname === '/';

  // Team mode определяется ТОЛЬКО из store (persisted в localStorage).
  // Переключение — ТОЛЬКО через ProfileScreen (с полной перезагрузкой).
  // Legacy /team/:id/* URL-ы обрабатываются компонентом TeamRedirect.
  
  // Глобальный WebSocket — real-time updates (tokens, generations, teams)
  // Подключается для ВСЕХ авторизованных пользователей
  useWebSocket({
    enabled: isAuthenticated
  });
  
  // Глобальный payment check - работает даже когда модальные окна закрыты
  const { setOnPaymentSuccess } = usePaymentCheck();
  
  // Service Worker update notification
  const { showUpdate, handleUpdate, handleDismiss } = useUpdateNotification();
  
  // DISABLED: Проверка временных достижений
  // useTimeAchievements();

  // Глобальный safety-net: предотвращает открытие файла в браузере при drag & drop
  // Без этого, если drop не попадает точно на React onDrop-обработчик (ре-рендер, overlay),
  // браузер выполняет дефолт — навигацию на файл
  useEffect(() => {
    const preventBrowserDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    document.addEventListener('dragover', preventBrowserDrop);
    document.addEventListener('drop', preventBrowserDrop);
    return () => {
      document.removeEventListener('dragover', preventBrowserDrop);
      document.removeEventListener('drop', preventBrowserDrop);
    };
  }, []);

  // Проверка авторизации: Telegram WebApp или Login Widget
  useEffect(() => {
    // Проверяем наличие данных авторизации (WebApp initData или Login Widget в localStorage)
    const hasAnyAuth = hasAuthData();
    
    const authenticated = !!(isReady && user && hasAnyAuth);
    setIsAuthenticated(authenticated);

    // Пропускаем страницы которые не требуют авторизации
    const isPublicPage = location.pathname === '/' || location.pathname.startsWith('/login') || location.pathname.startsWith('/access-denied') || location.pathname === '/pricing' || location.pathname === '/public-pricing' || location.pathname === '/privacy-policy' || location.pathname === '/public-offer' || location.pathname === '/cookie-policy';
    if (isPublicPage) {
      return;
    }

    // Если нет данных авторизации - редиректим на логин
    if (!hasAnyAuth) {
      // Проверяем, запущено ли приложение внутри Telegram Mini App
      // В Mini App НЕ редиректим - OAuth widget не работает в iframe
      const webAppData = (window as any).Telegram?.WebApp?.initData;
      const isInMiniApp = typeof webAppData === 'string' && webAppData.length > 0;
      
      if (!isInMiniApp) {
        // Непубличная страница + нет авторизации: уходим на лендинг, открываем логин модалкой поверх
        setTimeout(() => navigate('/?auth=login', { replace: true }), 0);
      }
      return;
    }
    
    // Если есть данные в localStorage, но user не распарсился - ждём или редиректим
    // НЕ редиректим если hasAnyAuth=true - данные есть, просто user может быть undefined
    // из-за того что useTelegramWebApp ещё не вернул объект
  }, [isReady, user, location.pathname, navigate]);

  // Устанавливаем классы контекста на html/body для CSS
  // (класс на html уже добавлен в index.html, но дублируем для надёжности)
  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;

    if (isInsideTelegram) {
      htmlEl.classList.add('is-telegram');
      htmlEl.classList.remove('is-website');
      bodyEl.classList.add('is-telegram');
      bodyEl.classList.remove('is-website');
    } else {
      htmlEl.classList.add('is-website');
      htmlEl.classList.remove('is-telegram');
      bodyEl.classList.add('is-website');
      bodyEl.classList.remove('is-telegram');
    }
    // Safe area переменные (--tg-safe-area-inset-*, --tg-content-safe-area-inset-*)
    // устанавливаются автоматически telegram-web-app.js на :root.
    // CSS использует --safe-top/bottom/left/right через max() для всех платформ.
  }, [isInsideTelegram]);

  // Fullscreen CSS class теперь управляется через telegram.ts initializeTelegramWebApp()
  // и syncFullscreenClass() (подписка на fullscreenChanged + toggle is-fullscreen/fullscreen)

  // Логирование выхода пользователя при закрытии приложения
  useEffect(() => {
    const handleLogout = () => {
      const initData = getInitData();
      const sessionId = localStorage.getItem('session_id');

      if (userData && (initData || sessionId)) {
        // Собираем заголовки в зависимости от типа авторизации
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        if (initData) {
          headers['X-Telegram-Init-Data'] = initData;
        }
        if (sessionId) {
          headers['X-Session-ID'] = sessionId;
        }

        // Используем fetch с keepalive для надежной отправки при закрытии
        fetch('/api/user/logout', {
          method: 'POST',
          headers,
          keepalive: true,
          body: JSON.stringify({})
        }).catch(() => {
          // Игнорируем ошибки при закрытии
        });
      }
    };

    // Слушаем событие закрытия окна
    window.addEventListener('beforeunload', handleLogout);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleLogout);
    };
  }, [userData]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    // Если нет пользователя, скрываем экран загрузки
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Инициализация и получение данных пользователя
    if (user) {
      // Сначала устанавливаем базовые данные из Telegram
      setUser({
        id: user.id.toString(),
        telegramId: user.id,
        username: user.username,
        firstName: user.first_name,
        lastName: user.last_name,
        tokens: 0,
        subscriptionTier: 'free',
      });

      // Проверяем, есть ли приглашение в команду в start_param (Telegram) или localStorage (Web)
      const tg = (window as any).Telegram?.WebApp;
      const startParam = tg?.initDataUnsafe?.start_param;
      let pendingTeamInviteCode: string | null = null;
      // Deeplink в режим команды: team_<uuid> или team_<uuid>_subscription
      // Используется в expiry-уведомлениях, чтобы открыть Mini App сразу в нужной команде.
      let pendingTeamSwitch: { teamId: string; goToSubscription: boolean } | null = null;

      // 1) Telegram Mini App deep link
      if (startParam && startParam.startsWith('invite_')) {
        const inviteCode = startParam.replace('invite_', '');
        const processedInvites = sessionStorage.getItem('processed_team_invites') || '';
        if (!processedInvites.includes(inviteCode)) {
          pendingTeamInviteCode = inviteCode;
        }
      } else if (startParam && startParam.startsWith('team_')) {
        // team_<uuid> | team_<uuid>_subscription
        const rest = startParam.slice('team_'.length);
        const goToSubscription = rest.endsWith('_subscription');
        const teamId = goToSubscription ? rest.slice(0, -'_subscription'.length) : rest;
        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (UUID_RE.test(teamId)) {
          pendingTeamSwitch = { teamId, goToSubscription };
        }
      }

      // 2) Web invite link (/invite/:code → saved to localStorage before login)
      if (!pendingTeamInviteCode) {
        const webInviteCode = localStorage.getItem('pending_team_invite');
        if (webInviteCode) {
          localStorage.removeItem('pending_team_invite');
          const processedInvites = sessionStorage.getItem('processed_team_invites') || '';
          if (!processedInvites.includes(webInviteCode)) {
            pendingTeamInviteCode = webInviteCode;
          }
        }
      }

      // Сначала инициализируем пользователя в БД (создается сразу при входе)
      initUser()
        .then((initData) => {
          let hasOtherModal = false;

          // Проверяем, зарегистрирован ли реферал
          if (initData.referralRegistered && initData.referrerInfo) {
            setReferrerInfo(initData.referrerInfo);
            setShowReferralModal(true);
            hasOtherModal = true;
          }

          // Проверяем, пришёл ли пользователь по рекламной кампании
          if (initData.campaignInfo) {
            setCampaignInfo(initData.campaignInfo);
            setShowCampaignModal(true);
            hasOtherModal = true;
          }

          // Показываем приглашение в команду после загрузки (как для рефералов)
          if (pendingTeamInviteCode) {
            setTeamInviteCode(pendingTeamInviteCode);
            setShowTeamInviteModal(true);
            hasOtherModal = true;
          }

          // Приветственное окно — только если нет других модалок и сервер не отметил показ
          if (!hasOtherModal && !initData.user?.welcomeModalShown) {
            setShowWelcomeModal(true);
          }
          
          // Затем загружаем полные данные с сервера, лимиты, команды и цены параллельно
          return Promise.all([
            fetchUser(true), // Форсируем обновление при первой загрузке
            fetchLimits(true),
            fetchTeams(), // Загружаем команды для восстановления activeTeam
            loadPricing() // Загружаем конфиг цен с бэкенда (Zustand store)
          ]);
        })
        .then(([userData]) => {
          // Deeplink: переключение в режим команды через start_param=team_<uuid>[_subscription].
          // Делаем после fetchTeams, чтобы валидировать членство по свежему списку команд.
          if (pendingTeamSwitch) {
            const teams = useTeamStore.getState().teams;
            const isMember = teams.some(t => t.id === pendingTeamSwitch!.teamId);
            if (isMember) {
              setActiveTeam(pendingTeamSwitch.teamId);
              if (pendingTeamSwitch.goToSubscription) {
                navigate('/account?screen=usage');
              }
            }
          }

          // Проверяем синхронизацию данных
          if (userData && !validateStorageSync(userData)) {
            clearUserStorage();
            // Перезагружаем данные
            return Promise.all([
              fetchUser(true),
              fetchLimits(true)
            ]);
          }
        })
        .catch(() => {
          // Если initUser не сработал, но пользователь авторизован —
          // всё равно показываем приветственное окно
          // Fallback: если initUser не сработал, проверяем стор
          const currentUser = useUserStore.getState().user;
          if (!currentUser?.welcomeModalShown) {
            setShowWelcomeModal(true);
          }
        });
      // Примечание: setIsLoading(false) НЕ вызывается здесь
      // LoadingScreen сам управляет своим жизненным циклом (3 секунды анимации)
    }
  }, [isReady, user, setUser, initUser, fetchUser, fetchLimits, fetchTeams, navigate]);

  // Синхронизация URL → store больше не нужна.
  // Legacy /team/:id/* URL-ы обрабатываются TeamRedirect (redirect + store update).

  // Синхронизация настроек голоса из БД в TTS store
  useEffect(() => {
    if (userData?.settings?.voice) {
      setVoice(userData.settings.voice as Voice);
    }
  }, [userData?.settings?.voice, setVoice]);



  // Устанавливаем User ID и свойства для Amplitude Analytics
  useEffect(() => {
    if (userData?.telegramId) {
      amplitude.setUserId(userData.telegramId.toString());
      
      // Создаём Identify объект для установки свойств пользователя
      const identifyObj = new amplitude.Identify();
      identifyObj.set('telegramId', userData.telegramId);
      identifyObj.set('subscriptionTier', userData.subscriptionTier || 'free');
      identifyObj.set('username', userData.username || 'anonymous');
      identifyObj.set('hasTokens', (userData.tokens ?? 0) > 0);
      identifyObj.set('firstName', userData.firstName || '');
      
      amplitude.identify(identifyObj);
    }
  }, [userData]);

  // Настройка callback для usePaymentCheck
  useEffect(() => {
    setOnPaymentSuccess(async () => {
      // Увеличенная задержка для гарантии что данные обновились на сервере
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      try {
        // Форсируем обновление user и limits, игнорируя кэш
        await Promise.all([
          fetchUser(true),
          fetchLimits(true)
        ]);
      } catch (error) {
        // При ошибке перезагружаем страницу
        setTimeout(() => window.location.reload(), 500);
      }
    });
  }, [fetchUser, fetchLimits, setOnPaymentSuccess]);

  // Глобальный слушатель события успешной оплаты
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      // Небольшая задержка для гарантии что данные обновились на сервере
      await new Promise(resolve => setTimeout(resolve, 800));
      
      try {
        // Форсируем обновление user и limits, игнорируя кэш
        await Promise.all([
          fetchUser(true),
          fetchLimits(true)
        ]);
      } catch (error) {
        // Ignore error
      }
    };
    
    window.addEventListener('payment-success', handlePaymentSuccess);
    
    return () => {
      window.removeEventListener('payment-success', handlePaymentSuccess);
    };
  }, [fetchUser, fetchLimits]);

  // Глобальный слушатель события смены подписки (включая отмену)
  useEffect(() => {
    const handleSubscriptionChanged = async () => {
      // Небольшая задержка для гарантии что данные обновились на сервере
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        // Форсируем обновление user и limits, игнорируя кэш
        await Promise.all([
          fetchUser(true),
          fetchLimits(true)
        ]);
      } catch (error) {
        // Ignore error
      }
    };
    
    window.addEventListener('subscription-changed', handleSubscriptionChanged);
    
    return () => {
      window.removeEventListener('subscription-changed', handleSubscriptionChanged);
    };
  }, [fetchUser, fetchLimits]);

  // Периодическая проверка подписки и токенов
  // Проверка каждые 2 минуты в обычном режиме (оптимизировано для производительности)
  // ВАЖНО: Используем обычный fetch (без force) чтобы использовать кэш
  // Более частая проверка (30 сек) для pending payments обрабатывается в usePaymentCheck
  useEffect(() => {
    if (!isReady) return;
    
    // Оптимизация: увеличиваем интервал до 2 минут
    // Быстрая проверка платежей происходит в usePaymentCheck (5 сек)
    const INTERVAL = 120000; // 2 минуты
    
    const intervalId = setInterval(async () => {
      // Проверяем только если вкладка активна - экономим ресурсы
      if (document.hidden) return;
      
      try {
        // Используем обычный fetch (без force) - кэш предотвратит избыточные запросы
        // fetchLimits вызывается без параметров, что означает forceRefresh = false
        await Promise.all([
          fetchUser(false), // Используем кэш для user тоже
          fetchLimits() // Limits используют кэш (30 секунд по умолчанию)
        ]);
      } catch {
        // Ignore error
      }
    }, INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [isReady, fetchUser, fetchLimits]);

  // Обновление токенов при возврате фокуса на вкладку
  // Важно для случаев: пополнение от админа, оплата через бота, внешние изменения баланса
  useEffect(() => {
    if (!isReady) return;

    let lastHiddenTime = 0;
    const MIN_HIDDEN_TIME = 3000; // Минимум 3 секунды в фоне для обновления

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Запоминаем время ухода в фон
        lastHiddenTime = Date.now();
      } else {
        // Вернулись на вкладку - проверяем, достаточно ли долго были в фоне
        const hiddenDuration = Date.now() - lastHiddenTime;
        if (lastHiddenTime > 0 && hiddenDuration >= MIN_HIDDEN_TIME) {
          try {
            // Принудительно обновляем данные пользователя (токены)
            await fetchUser(true);
          } catch {
            // Ignore error
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isReady, fetchUser]);

  // Проверка версии конфига цен при фокусе окна (обновляет кэш если админ изменил цены)
  useEffect(() => {
    return initPricingVersionCheck();
  }, []);

  // Сброс currentSessionId при закрытии/минимизации приложения для секретных чатов
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Если приложение скрыто (пользователь закрыл/минимизировал) и текущая сессия секретная
      if (document.hidden && isCurrentSessionSecret) {
        setCurrentSessionId(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isCurrentSessionSecret, setCurrentSessionId]);

  // Отслеживание низкого баланса токенов для платных и free пользователей
  useEffect(() => {
    // Проверяем только если данные пользователя загружены
    if (!userData) return;
    
    // В режиме команды не показываем уведомление о личных токенах
    // (пользователь использует токены команды, а не свои)
    if (isTeamMode && activeTeam) {
      setShowLowTokensNotification(false);
      setIsFreeUserNotification(false);
      return;
    }

    const subscriptionTier = userData.subscriptionTier;
    const tokens = userData.tokens ?? 0;

    // Проверяем тип пользователя
    const isFreeUser = !subscriptionTier || subscriptionTier === 'free';
    const isPaidSubscriber = subscriptionTier && subscriptionTier !== 'free';

    // Пороги для показа уведомления
    const LOW_TOKEN_THRESHOLD_PAID = 200; // Для платных - 200 токенов
    const LOW_TOKEN_THRESHOLD_FREE = 20;  // Для free - 20 токенов

    // Проверяем, не было ли уведомление показано за последние 24 часа
    const storageKey = isFreeUser ? 'free_low_tokens_notification_dismissed_at' : 'low_tokens_notification_dismissed_at';
    const lastDismissed = localStorage.getItem(storageKey);
    const oneDayMs = 24 * 60 * 60 * 1000; // 24 часа в миллисекундах
    const canShowNotification = !lastDismissed || (Date.now() - parseInt(lastDismissed, 10)) > oneDayMs;

    // Для free пользователей - показываем когда токенов < 20
    if (isFreeUser && tokens < LOW_TOKEN_THRESHOLD_FREE && tokens > 0 && canShowNotification) {
      setIsFreeUserNotification(true);
      setShowLowTokensNotification(true);
    }
    // Для платных подписчиков - показываем когда токенов < 200
    else if (isPaidSubscriber && tokens < LOW_TOKEN_THRESHOLD_PAID && canShowNotification) {
      setIsFreeUserNotification(false);
      setShowLowTokensNotification(true);
    } else {
      setShowLowTokensNotification(false);
      setIsFreeUserNotification(false);
    }
  }, [userData, isTeamMode, activeTeam]);

  return (
    <>
      {isLoading && <LoadingScreen onLoadingComplete={() => setIsLoading(false)} />}
      <div ref={appRef} className={`app ${!isLoginPage ? 'has-desktop-header' : ''}`} style={{ opacity: isLoading ? 0 : 1 }}>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          {/* Main routes — team mode определяется стором, не URL */}
          <Route path="/chat" element={
            <TeamModeWrapper mode="personal">
              <ChatPage />
            </TeamModeWrapper>
          } />
          <Route path="/image" element={
            <TeamModeWrapper mode="personal">
              <ImagePage />
            </TeamModeWrapper>
          } />
          <Route path="/video" element={
            <TeamModeWrapper mode="personal">
              <VideoPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools" element={
            <TeamModeWrapper mode="personal">
              <ToolsPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/upscale" element={
            <TeamModeWrapper mode="personal">
              <UpscalePage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/image-edit" element={
            <TeamModeWrapper mode="personal">
              <ImageEditPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/visionary" element={
            <TeamModeWrapper mode="personal">
              <VisionaryStudioPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/avatars" element={
            <TeamModeWrapper mode="personal">
              <AvatarsPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/wardrobe" element={
            <TeamModeWrapper mode="personal">
              <WardrobePage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/video-edit" element={<Navigate to="/video?tab=edit" replace />} />
          <Route path="/tools/motion-control" element={
            <TeamModeWrapper mode="personal">
              <MotionControlPage />
            </TeamModeWrapper>
          } />
          <Route path="/tools/video-restyle" element={
            <TeamModeWrapper mode="personal">
              <VideoRestylePage />
            </TeamModeWrapper>
          } />
          {/* Web invite link — saves code & redirects */}
          <Route path="/invite/:code" element={<InviteRedirectPage />} />
          {/* Legacy team routes → redirect to clean URL + set store */}
          <Route path="/team/:teamId/*" element={<TeamRedirect />} />
          {/* Common routes */}
          <Route path="/account" element={<AccountPage />} />
          <Route path="/merge-account" element={<MergeAccountPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/tokens" element={<TokensPage />} />
          <Route path="/referrals" element={<ReferralsPage />} />
          <Route path="/projects" element={<Navigate to="/gallery" replace />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/dev/broadcast-card" element={<BroadcastCardPreviewPage />} />
          <Route path="/dev/expiry-card" element={<BroadcastCardPreviewPage />} />
          {/* Public pricing page - доступна без авторизации */}
          <Route path="/pricing" element={<PublicPricingPage />} />
          <Route path="/public-pricing" element={<PublicPricingPage />} />
          {/* Legal pages - доступны без авторизации */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/public-offer" element={<PublicOfferPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/consent" element={<ConsentPage />} />
          {/* Landing page - всегда показывается при первом входе в сессию */}
          <Route path="/" element={<LandingPage />} />
        </Routes>
        </Suspense>
        {/* Desktop Header - показывается на ПК (>= 1024px) на всех страницах кроме login */}
        {!isLoginPage && <DesktopHeader />}
        {/* Mobile Public Header - показывается на мобильных для неавторизованных на landing и pricing */}
        {!isAuthenticated && (isLandingPage || location.pathname === '/pricing') && <MobilePublicHeader />}
        {/* Bottom Nav - показывается на мобильных (< 1024px) для авторизованных на всех страницах кроме login */}
        {isAuthenticated && !isLoginPage && <BottomNav />}
        <Toast />
        <NotificationManager />
      </div>

      {/* Login modal overlay — рендерится поверх любой страницы при ?auth=login */}
      {showLoginModal && !isLoginPage && (
        <Suspense fallback={null}>
          <LoginPage />
        </Suspense>
      )}
      
      {/* Модалки показываются только для авторизованных пользователей вне страницы логина */}
      {isAuthenticated && !isLoginPage && (
        <>
          {/* Приветственное модальное окно для рефералов */}
          {showReferralModal && referrerInfo && (
            <ReferralWelcomeModal 
              referrerInfo={referrerInfo}
              onClose={() => setShowReferralModal(false)}
            />
          )}
          
          {/* Приветственное модальное окно для рекламных кампаний */}
          {showCampaignModal && campaignInfo && (
            <CampaignWelcomeModal 
              campaignInfo={campaignInfo}
              onClose={() => setShowCampaignModal(false)}
            />
          )}
          
          {/* Приглашение в команду */}
          {showTeamInviteModal && teamInviteCode && (
            <TeamInviteWelcomeModal 
              inviteCode={teamInviteCode}
              onClose={() => {
                // Помечаем приглашение как обработанное
                const processedInvites = sessionStorage.getItem('processed_team_invites') || '';
                sessionStorage.setItem('processed_team_invites', processedInvites + ',' + teamInviteCode);
                setShowTeamInviteModal(false);
                setTeamInviteCode(null);
              }}
              onAccepted={async (teamInfo) => {
                // Помечаем приглашение как обработанное
                const processedInvites = sessionStorage.getItem('processed_team_invites') || '';
                sessionStorage.setItem('processed_team_invites', processedInvites + ',' + teamInviteCode);
                // Обновляем стор и делаем полную перезагрузку — URL чистый
                await fetchTeams();
                setActiveTeam(teamInfo.teamId);
                window.location.href = '/chat';
              }}
            />
          )}
          
          {/* Приветственное окно с выбором темы */}
          {showWelcomeModal && (
            <WelcomeModal
              onClose={() => {
                apiClient.user.markWelcomeShown().catch(() => {});
                setShowWelcomeModal(false);
              }}
            />
          )}

          {/* Уведомление о низком балансе токенов */}
          {showLowTokensNotification && userData && (
            <LowTokensNotification 
              tokens={userData.tokens ?? 0}
              isFreeUser={isFreeUserNotification}
              onClose={() => {
                setShowLowTokensNotification(false);
                setIsFreeUserNotification(false);
                // Запоминаем время закрытия - показываем не чаще раза в день
                // Разные ключи для free и платных пользователей
                const storageKey = isFreeUserNotification 
                  ? 'free_low_tokens_notification_dismissed_at' 
                  : 'low_tokens_notification_dismissed_at';
                localStorage.setItem(storageKey, Date.now().toString());
              }}
            />
          )}
        </>
      )}
      
      {/* Уведомление об обновлении приложения (Service Worker) */}
      {showUpdate && (
        <UpdateNotification
          onUpdate={handleUpdate}
          onDismiss={handleDismiss}
        />
      )}

      {/* Cookie consent banner — показывается только на .ru, если пользователь ещё не дал согласия */}
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
